import { useEffect } from 'react';
import { FileText, Users, Shield, AlertTriangle } from 'lucide-react';

const Terms = () => {
  useEffect(() => {
    document.title = 'Terms of Service - FemSportNet';
  }, []);

  return (
    <div className="bg-white dark:bg-gray-900 min-h-screen transition-colors duration-200">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-green-600 to-green-800 py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Terms of Service
            </h1>
            <p className="text-xl text-green-100 mb-8">
              Please read these terms carefully before using FemSportNet. By using our platform, 
              you agree to be bound by these terms.
            </p>
            <p className="text-green-100">
              Last updated: March 15, 2024
            </p>
          </div>
        </div>
      </section>

      {/* Terms Content */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 space-y-8">
              
              {/* Acceptance of Terms */}
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 flex items-center">
                  <FileText className="h-6 w-6 text-green-500 mr-2" />
                  Acceptance of Terms
                </h2>
                
                <p className="text-gray-600 dark:text-gray-300 mb-4">
                  By accessing or using FemSportNet ("the Platform"), you agree to be bound by these 
                  Terms of Service ("Terms"). If you do not agree to these Terms, please do not use 
                  our Platform.
                </p>
                
                <p className="text-gray-600 dark:text-gray-300">
                  These Terms constitute a legally binding agreement between you and FemSportNet, Inc. 
                  ("Company," "we," "us," or "our").
                </p>
              </div>

              {/* Eligibility */}
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                  Eligibility
                </h2>
                
                <p className="text-gray-600 dark:text-gray-300 mb-4">
                  To use FemSportNet, you must:
                </p>
                
                <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-1">
                  <li>Be at least 13 years of age</li>
                  <li>Provide accurate and complete registration information</li>
                  <li>Maintain the security of your account credentials</li>
                  <li>Comply with all applicable laws and regulations</li>
                  <li>Not be prohibited from using our services under applicable law</li>
                </ul>
              </div>

              {/* User Accounts */}
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 flex items-center">
                  <Users className="h-6 w-6 text-green-500 mr-2" />
                  User Accounts
                </h2>
                
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                      Account Creation
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300">
                      You are responsible for maintaining the confidentiality of your account information 
                      and for all activities that occur under your account. You must notify us immediately 
                      of any unauthorized use of your account.
                    </p>
                  </div>
                  
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                      Account Verification
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300">
                      We may verify certain accounts to ensure authenticity. Verified status is granted 
                      at our discretion and may be revoked if verification requirements are no longer met.
                    </p>
                  </div>
                  
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                      Account Termination
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300">
                      You may delete your account at any time. We reserve the right to suspend or 
                      terminate accounts that violate these Terms or our Community Guidelines.
                    </p>
                  </div>
                </div>
              </div>

              {/* Acceptable Use */}
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 flex items-center">
                  <Shield className="h-6 w-6 text-green-500 mr-2" />
                  Acceptable Use
                </h2>
                
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                      Permitted Uses
                    </h3>
                    <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-1">
                      <li>Create and maintain a professional athlete profile</li>
                      <li>Connect and communicate with other users</li>
                      <li>Share sports-related content and achievements</li>
                      <li>Use marketplace features for legitimate transactions</li>
                      <li>Access educational resources and opportunities</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                      Prohibited Uses
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300 mb-2">You may not:</p>
                    <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-1">
                      <li>Post false, misleading, or fraudulent information</li>
                      <li>Harass, bully, or discriminate against other users</li>
                      <li>Share inappropriate, offensive, or illegal content</li>
                      <li>Attempt to hack, disrupt, or damage our systems</li>
                      <li>Use automated tools to access or scrape our platform</li>
                      <li>Impersonate another person or entity</li>
                      <li>Violate intellectual property rights</li>
                      <li>Engage in spam or unsolicited marketing</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Content and Intellectual Property */}
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                  Content and Intellectual Property
                </h2>
                
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                      Your Content
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300">
                      You retain ownership of content you post on FemSportNet. By posting content, 
                      you grant us a non-exclusive, worldwide, royalty-free license to use, display, 
                      and distribute your content on our platform.
                    </p>
                  </div>
                  
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                      Our Content
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300">
                      FemSportNet and its content are protected by copyright, trademark, and other 
                      intellectual property laws. You may not copy, modify, or distribute our content 
                      without permission.
                    </p>
                  </div>
                  
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                      Copyright Infringement
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300">
                      We respect intellectual property rights and will respond to valid copyright 
                      infringement notices in accordance with the Digital Millennium Copyright Act (DMCA).
                    </p>
                  </div>
                </div>
              </div>

              {/* Marketplace Terms */}
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                  Marketplace Terms
                </h2>
                
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                      Seller Responsibilities
                    </h3>
                    <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-1">
                      <li>Provide accurate product descriptions and pricing</li>
                      <li>Honor all sales and shipping commitments</li>
                      <li>Comply with applicable laws and regulations</li>
                      <li>Handle customer service and returns professionally</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                      Buyer Responsibilities
                    </h3>
                    <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-1">
                      <li>Provide accurate payment and shipping information</li>
                      <li>Complete transactions in good faith</li>
                      <li>Follow seller's return and refund policies</li>
                      <li>Report any issues or disputes promptly</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                      Platform Role
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300">
                      FemSportNet facilitates transactions but is not a party to them. We do not 
                      guarantee the quality, safety, or legality of items listed, or the ability 
                      of sellers to complete transactions.
                    </p>
                  </div>
                </div>
              </div>

              {/* Privacy and Data */}
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                  Privacy and Data
                </h2>
                
                <p className="text-gray-600 dark:text-gray-300">
                  Your privacy is important to us. Our Privacy Policy explains how we collect, use, 
                  and protect your information. By using FemSportNet, you also agree to our Privacy Policy.
                </p>
              </div>

              {/* Disclaimers */}
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 flex items-center">
                  <AlertTriangle className="h-6 w-6 text-yellow-500 mr-2" />
                  Disclaimers and Limitations
                </h2>
                
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                      Service Availability
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300">
                      We strive to maintain platform availability but cannot guarantee uninterrupted 
                      service. We may temporarily suspend service for maintenance or updates.
                    </p>
                  </div>
                  
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                      Limitation of Liability
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300">
                      To the maximum extent permitted by law, FemSportNet shall not be liable for 
                      any indirect, incidental, special, or consequential damages arising from your 
                      use of our platform.
                    </p>
                  </div>
                  
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                      Indemnification
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300">
                      You agree to indemnify and hold harmless FemSportNet from any claims, damages, 
                      or expenses arising from your use of our platform or violation of these Terms.
                    </p>
                  </div>
                </div>
              </div>

              {/* Governing Law */}
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                  Governing Law and Disputes
                </h2>
                
                <p className="text-gray-600 dark:text-gray-300 mb-4">
                  These Terms are governed by the laws of the State of California, without regard 
                  to conflict of law principles. Any disputes arising from these Terms or your use 
                  of FemSportNet will be resolved through binding arbitration.
                </p>
                
                <p className="text-gray-600 dark:text-gray-300">
                  Before initiating arbitration, you agree to first contact us to attempt to resolve 
                  the dispute informally.
                </p>
              </div>

              {/* Changes to Terms */}
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                  Changes to Terms
                </h2>
                
                <p className="text-gray-600 dark:text-gray-300">
                  We may update these Terms from time to time. We will notify you of material changes 
                  by posting the updated Terms on our platform and updating the "Last updated" date. 
                  Your continued use of FemSportNet after changes become effective constitutes acceptance 
                  of the new Terms.
                </p>
              </div>

              {/* Contact Information */}
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                  Contact Us
                </h2>
                
                <p className="text-gray-600 dark:text-gray-300 mb-4">
                  If you have questions about these Terms of Service, please contact us:
                </p>
                
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                  <p className="text-gray-600 dark:text-gray-300">
                    <strong>Email:</strong> legal@femsportnet.com<br />
                    <strong>Address:</strong> 123 Tech Street, Suite 400, San Francisco, CA 94105<br />
                    <strong>Phone:</strong> +1 (555) 123-4567
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Terms;